import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useContactModal } from '../App';
import {
  FaStar, FaPhone, FaArrowRight, FaChevronDown, FaOm
} from 'react-icons/fa';
import { GiMeditation } from 'react-icons/gi';
import servicesData from '../data/services';
import './Services.css';

const services = servicesData.map(s => ({
  icon: React.createElement(s.icon),
  title: s.title,
  slug: s.slug,
  desc: s.desc,
  highlights: s.highlights,
}));

const faqs = [
  { q: 'What information do I need to provide for a consultation?', a: 'You need to provide your exact date of birth, time of birth, and place of birth. These details are essential for preparing an accurate horoscope and planetary analysis.' },
  { q: 'How long does a typical consultation session take?', a: 'A standard consultation session takes 30-45 minutes. However, detailed horoscope analysis or complex cases may require up to 60 minutes for thorough examination.' },
  { q: 'Can consultations be done remotely?', a: 'Yes, absolutely! We offer phone, video call, and WhatsApp consultations for clients across the world. The accuracy of predictions remains the same regardless of the consultation mode.' },
  { q: 'How soon can I see results from the suggested remedies?', a: 'Remedies typically start showing effects within 21-45 days of sincere practice. However, the timeline varies based on the planetary period, the nature of the issue, and the consistency of performing the remedies.' },
  { q: 'Is medical astrology a replacement for medical treatment?', a: 'No, medical astrology is complementary to modern medicine. It helps identify the cosmic root cause of health issues and provides supportive remedies. Always continue your prescribed medical treatment alongside astrological remedies.' },
  { q: 'What is the difference between Vedic and KP Astrology?', a: 'Vedic astrology uses traditional sign-based analysis, while KP (Krishnamurthy Paddhati) uses a sub-lord system for more precise timing of events. Acharya Arun uses both systems together for the most accurate predictions.' },
];

function FaqItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`bg-white rounded-xl border transition-all duration-300 overflow-hidden ${open ? 'border-accent-gold shadow-[0_4px_20px_rgba(167,139,250,0.1)]' : 'border-warm-light-gray hover:border-accent-gold/30'}`}>
      <button className="w-full flex justify-between items-center py-5 px-6 max-md:py-4 max-md:px-5 bg-transparent border-none cursor-pointer font-body text-base max-md:text-[0.92rem] font-semibold text-primary-dark text-left gap-4" onClick={() => setOpen(!open)}>
        <span>{faq.q}</span>
        <FaChevronDown className={`text-[0.8rem] text-accent-gold transition-transform duration-300 flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-400 ${open ? 'max-h-[300px] pb-5 px-6 max-md:px-5' : 'max-h-0'}`}>
        <p className="text-[0.92rem] text-warm-gray leading-[1.7]">{faq.a}</p>
      </div>
    </div>
  );
}

const Services = () => {
  const { openContactModal } = useContactModal();

  return (
    <div>
      {/* Hero Banner */}
      <section className="page-hero">
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(40)].map((_, i) => (
            <div key={i} className="page-hero-star" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
            }} />
          ))}
        </div>
        <div className="page-hero-content">
          <div className="hero-badge-sm"><FaStar /> Divine Services</div>
          <h1>Our Cosmic Services</h1>
          <p>Comprehensive astrology and healing services to guide every aspect of your life journey</p>
          <div className="breadcrumb"><Link to="/">Home</Link> <span>/</span> <span>Services</span></div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">
            <div className="flex-shrink-0">
              <div className="w-[280px] h-[280px] max-md:w-[220px] max-md:h-[220px] rounded-full overflow-hidden border-[3px] border-accent-gold/20 shadow-[0_12px_40px_rgba(12,10,32,0.2),0_0_30px_rgba(167,139,250,0.12)]">
                <img src="/assets/profile-img-1.png" alt="Acharya Arun" className="w-full h-full object-cover object-top" />
              </div>
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl text-primary-dark font-heading mb-4">Guided by Acharya Arun</h2>
              <p className="text-[0.95rem] text-warm-text-body leading-[1.8]">
                With over 28 years of expertise in Vedic and KP astrology, Acharya Arun offers personalized cosmic guidance for every aspect of your life. Each service is delivered with deep knowledge, compassion, and a commitment to your well-being and prosperity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 bg-warm-off-white">
        <div className="container-custom">
          <div className="flex flex-col gap-10">
            {services.map((service, index) => (
              <div key={index} className={`service-detail-card group flex items-center gap-10 max-md:gap-6 bg-white rounded-[20px] p-11 max-md:p-6 border border-warm-light-gray transition-all duration-400 relative overflow-hidden max-md:flex-col hover:shadow-[0_20px_50px_rgba(12,10,32,0.1),0_0_0_1px_rgba(167,139,250,0.08)] ${index % 2 !== 0 ? 'md:flex-row-reverse hover:md:-translate-x-[5px]' : 'hover:md:translate-x-[5px]'}`}>
                <div className="flex-shrink-0 text-center relative">
                  <div className="w-[100px] h-[100px] rounded-full flex items-center justify-center text-[2.5rem] text-primary-medium transition-all duration-400 border border-accent-gold/10 group-hover:text-white group-hover:shadow-gold-md group-hover:scale-105" style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.08), rgba(249,115,22,0.06))' }}>
                    {service.icon}
                  </div>
                  <span className="block mt-2.5 font-heading text-[0.85rem] text-accent-saffron/60">0{index + 1}</span>
                </div>
                <div>
                  <h3 className="text-2xl text-primary-dark mb-4">{service.title}</h3>
                  <p className="text-[0.95rem] text-warm-text-body mb-5 leading-[1.8]">{service.desc}</p>
                  <ul className="flex flex-wrap gap-2.5">
                    {service.highlights.map((h, i) => (
                      <li key={i} className="flex items-center gap-1.5 py-2 px-4 border border-accent-gold/10 rounded-[25px] text-[0.8rem] text-primary-medium font-medium transition-all duration-300 hover:bg-accent-gold/[0.12] hover:border-accent-gold/25" style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.06), rgba(249,115,22,0.04))' }}>
                        <FaStar className="text-accent-saffron text-[0.6rem]" /> {h}
                      </li>
                    ))}
                  </ul>
                  <Link to={`/services/${service.slug}`} className="inline-flex items-center gap-2 mt-5 text-[0.88rem] font-semibold text-accent-gold hover:text-accent-saffron transition-colors duration-300">
                    Learn More <FaArrowRight className="text-xs" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="section-title">
            <h2>How It Works</h2>
            <p>Simple steps to begin your cosmic healing journey</p>
            <div className="decorative-line"><span></span><div className="dot"></div><span></span></div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-5">
            {[
              { num: '01', title: 'Book Consultation', desc: 'Reach out via call or contact form with your birth details and concerns.' },
              { num: '02', title: 'Chart Analysis', desc: 'Acharya Arun carefully analyzes your birth chart using Vedic & KP systems.' },
              { num: '03', title: 'Receive Guidance', desc: 'Get detailed predictions, remedies, and personalized cosmic guidance for your life.' },
            ].map((step, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="text-2xl text-accent-gold/50 md:rotate-0 rotate-90"><GiMeditation /></div>}
                <div className="text-center py-11 px-[30px] max-md:py-8 max-md:px-5 bg-warm-off-white rounded-[20px] flex-1 max-w-[300px] transition-all duration-400 border border-transparent hover:border-accent-gold/30 hover:-translate-y-2 hover:shadow-[0_15px_40px_rgba(12,10,32,0.08)]">
                  <div className="font-heading text-[2.5rem] font-bold bg-gradient-to-r from-accent-gold to-accent-saffron bg-clip-text text-transparent mb-4">{step.num}</div>
                  <h4 className="text-lg text-primary-dark mb-2.5">{step.title}</h4>
                  <p className="text-[0.85rem] text-warm-gray">{step.desc}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-warm-off-white">
        <div className="container-custom">
          <div className="section-title">
            <h2>Frequently Asked Questions</h2>
            <p>Common questions about our astrological services and consultation process</p>
            <div className="decorative-line"><span></span><div className="dot"></div><span></span></div>
          </div>

          <div className="max-w-[800px] mx-auto flex flex-col gap-4">
            {faqs.map((faq, i) => (
              <FaqItem key={i} faq={faq} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-cosmic text-center">
        <div className="container-custom">
          <FaOm className="text-[2.5rem] text-accent-gold mb-4 mx-auto" />
          <h2 className="text-[2.2rem] max-md:text-[1.6rem] text-white mb-2.5 font-heading">Ready for Your Cosmic Consultation?</h2>
          <p className="text-[1.05rem] text-white/70">Take the first step towards a balanced, prosperous, and healthy life.</p>
          <div className="flex gap-5 justify-center mt-8 flex-wrap">
            <a href="tel:+918084768303" className="btn-primary"><FaPhone /> Call Now</a>
            <button onClick={openContactModal} className="btn-secondary !border-white !text-white hover:!bg-transparent hover:!border-accent-gold hover:!text-white">
              Book Appointment <FaArrowRight />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
